import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

interface Proposal {
  id?: string;
  question_id: string;
  course_name: string | null;
  lesson_id: string | null;
  lesson_name: string | null;
  question_preview: string;
  current_concept_tag: string | null;
  proposed_concept_tag: string;
  confidence_score: number;
  evidence: { matched_keywords: string[] };
  reason: string;
  status?: string;
}

interface Coverage { total: number; tagged: number; untagged: number; percent_tagged: number }
interface CourseCoverage { course_name: string; total: number; tagged: number; pct: number }
interface StatusCounts { pending_review: number; approved: number; rejected: number; applied: number }

export default function AdminConceptTagReview() {
  const [courseFilter, setCourseFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending_review" | "approved" | "rejected" | "applied">("pending_review");
  const [minConfidence, setMinConfidence] = useState(0);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [courseCoverage, setCourseCoverage] = useState<CourseCoverage[]>([]);
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({ pending_review: 0, approved: 0, rejected: 0, applied: 0 });
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [rejected, setRejected] = useState<Record<string, boolean>>({});

  const loadCoverage = async () => {
    const { count: total } = await supabase.from("questions").select("*", { count: "exact", head: true });
    const { count: tagged } = await supabase.from("questions").select("*", { count: "exact", head: true }).not("concept_tag", "is", null);
    const t = total ?? 0, ta = tagged ?? 0;
    setCoverage({ total: t, tagged: ta, untagged: t - ta, percent_tagged: t ? Math.round((ta / t) * 1000) / 10 : 0 });

    // Per-course coverage (top 20 by tagged). questions has no course_name column —
    // the course is resolved through the lesson.
    const { data: qrows } = await supabase
      .from("questions")
      .select("concept_tag, lessons(course_name)")
      .limit(10000);
    const map = new Map<string, { total: number; tagged: number }>();
    for (const r of (qrows ?? [])) {
      const lesson = (r as AnyValue).lessons as { course_name?: string | null } | null;
      const k = lesson?.course_name ?? "—";
      const m = map.get(k) ?? { total: 0, tagged: 0 };
      m.total++;
      if ((r as AnyValue).concept_tag) m.tagged++;
      map.set(k, m);
    }
    const cc = Array.from(map.entries())
      .map(([course_name, v]) => ({ course_name, total: v.total, tagged: v.tagged, pct: v.total ? Math.round((v.tagged / v.total) * 1000) / 10 : 0 }))
      .sort((a, b) => b.tagged - a.tagged)
      .slice(0, 20);
    setCourseCoverage(cc);

    // Proposal status counts
    const statuses: Array<keyof StatusCounts> = ["pending_review", "approved", "rejected", "applied"];
    const counts: StatusCounts = { pending_review: 0, approved: 0, rejected: 0, applied: 0 };
    for (const s of statuses) {
      const { count } = await supabase
        .from("question_concept_tag_proposals")
        .select("*", { count: "exact", head: true })
        .eq("status", s);
      counts[s] = count ?? 0;
    }
    setStatusCounts(counts);
  };

  const loadPersisted = async () => {
    setLoading(true);
    try {
      let q = supabase
        .from("question_concept_tag_proposals")
        .select("*")
        .order("confidence_score", { ascending: false })
        .limit(limit);
      if (statusFilter !== "all") q = q.eq("status", statusFilter);
      if (courseFilter) q = q.eq("course_name", courseFilter);
      if (tagFilter) q = q.eq("proposed_concept_tag", tagFilter);
      if (minConfidence > 0) q = q.gte("confidence_score", minConfidence);
      const { data, error } = await q;
      if (error) throw error;
      let rows = (data ?? []) as AnyValue as Proposal[];
      if (search) {
        const s = search.toLowerCase();
        rows = rows.filter((p) =>
          (p.question_preview ?? "").toLowerCase().includes(s) ||
          (p.lesson_name ?? "").toLowerCase().includes(s)
        );
      }
      setProposals(rows);
      setSelected({});
      setRejected({});
      toast.success(`Loaded ${rows.length} persisted proposals`);
    } catch (e: unknown) {
      toast.error(getErrorMessage(e) ?? "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadCoverage(); }, []);

  const runProposal = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("propose-concept-tags", {
        body: { course_name: courseFilter || undefined, limit, only_untagged: true },
      });
      if (error) throw error;
      setProposals(data?.proposals ?? []);
      setSelected({});
      setRejected({});
      if (data?.coverage) setCoverage(data.coverage);
      toast.success(`Scanned ${data?.scanned ?? 0}, proposed ${data?.proposed ?? 0}`);
    } catch (e: unknown) {
      toast.error(getErrorMessage(e) ?? "Proposal failed");
    } finally { setLoading(false); }
  };

  const approveAll = () => {
    const m: Record<string, boolean> = {};
    proposals.forEach((p) => { if (!rejected[p.question_id]) m[p.question_id] = true; });
    setSelected(m);
  };

  const rejectAllVisible = () => {
    const m: Record<string, boolean> = {};
    proposals.forEach((p) => { m[p.question_id] = true; });
    setRejected(m);
    setSelected({});
  };

  const bulkUpdateStatus = async (next: "approved" | "rejected") => {
    const ids = proposals
      .filter((p) => next === "approved" ? selected[p.question_id] && !rejected[p.question_id] : rejected[p.question_id])
      .map((p) => (p).id)
      .filter(Boolean);
    if (!ids.length) { toast.error("Nothing selected"); return; }
    const { error } = await supabase
      .from("question_concept_tag_proposals")
      .update({ status: next, reviewed_at: new Date().toISOString() })
      .in("id", ids);
    if (error) { toast.error(error.message); return; }
    toast.success(`Marked ${ids.length} as ${next}`);
    await loadCoverage();
    await loadPersisted();
  };

  const exportCsv = () => {
    const header = ["course_name","lesson_name","proposed_concept_tag","confidence_score","status","question_preview"];
    const rows = proposals.map((p) => header.map((h) => JSON.stringify((p)[h] ?? "")).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `concept-tag-proposals-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const applyApproved = async () => {
    const picked = proposals.filter((p) => selected[p.question_id] && !rejected[p.question_id]);
    if (picked.length === 0) { toast.error("Nothing approved"); return; }
    if (picked.length > 50) {
      const ok = window.confirm(`You're about to apply ${picked.length} tags in one batch. Continue?`);
      if (!ok) return;
    }
    // Prefer persisted proposal_ids when present; fall back to items.
    const proposal_ids = picked.map((p) => (p).id).filter(Boolean);
    const items = picked
      .filter((p) => !(p).id)
      .map((p) => ({ question_id: p.question_id, concept_tag: p.proposed_concept_tag }));
    setApplying(true);
    try {
      const { data, error } = await supabase.functions.invoke("apply-concept-tags", {
        body: { items, proposal_ids },
      });
      if (error) throw error;
      toast.success(`Applied ${data?.applied ?? 0}, rejected ${data?.rejected ?? 0}, skipped ${data?.skipped ?? 0}`);
      await loadCoverage();
      // remove applied rows from list
      const appliedIds = new Set((data?.results ?? []).filter((r: AnyValue) => r.status === "applied").map((r: AnyValue) => r.question_id));
      setProposals((prev) => prev.filter((p) => !appliedIds.has(p.question_id)));
      setSelected({});
    } catch (e: unknown) {
      toast.error(getErrorMessage(e) ?? "Apply failed");
    } finally { setApplying(false); }
  };

  const approvedCount = useMemo(
    () => proposals.filter((p) => selected[p.question_id] && !rejected[p.question_id]).length,
    [proposals, selected, rejected],
  );

  return (
    <div className="container mx-auto max-w-5xl p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Concept-Tag Review</h1>
        <p className="text-sm text-muted-foreground">
          Admin-only review-first backfill. Proposals are read-only until you approve and apply.
        </p>
      </header>

      {coverage && (
        <Card className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Total questions" value={coverage.total} />
            <Metric label="Tagged" value={coverage.tagged} />
            <Metric label="Untagged" value={coverage.untagged} />
            <Metric label="% Tagged" value={`${coverage.percent_tagged}%`} />
          </div>
          <div className="w-full h-2 bg-muted rounded">
            <div className="h-2 bg-primary rounded" style={{ width: `${coverage.percent_tagged}%` }} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <Metric label="Pending" value={statusCounts.pending_review} />
            <Metric label="Approved" value={statusCounts.approved} />
            <Metric label="Rejected" value={statusCounts.rejected} />
            <Metric label="Applied" value={statusCounts.applied} />
          </div>
        </Card>
      )}

      {courseCoverage.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-2">Top courses by tagged count</h3>
          <div className="space-y-1">
            {courseCoverage.map((c) => (
              <div key={c.course_name} className="flex items-center gap-2 text-xs">
                <div className="w-56 truncate">{c.course_name}</div>
                <div className="flex-1 h-2 bg-muted rounded">
                  <div className="h-2 bg-primary rounded" style={{ width: `${c.pct}%` }} />
                </div>
                <div className="w-24 text-right tabular-nums">{c.tagged}/{c.total} ({c.pct}%)</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Filter by course (optional)</label>
            <Input value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} placeholder="e.g. Investing Fundamentals" />
          </div>
          <div className="w-40">
            <label className="text-xs text-muted-foreground">Proposed tag</label>
            <Input value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} placeholder="e.g. compound_interest" />
          </div>
          <div className="w-32">
            <label className="text-xs text-muted-foreground">Status</label>
            <select className="w-full h-10 rounded border bg-background px-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AnyValue)}>
              <option value="all">all</option>
              <option value="pending_review">pending_review</option>
              <option value="approved">approved</option>
              <option value="rejected">rejected</option>
              <option value="applied">applied</option>
            </select>
          </div>
          <div className="w-32">
            <label className="text-xs text-muted-foreground">Min confidence</label>
            <Input type="number" value={minConfidence} onChange={(e) => setMinConfidence(Number(e.target.value) || 0)} />
          </div>
          <div className="w-44">
            <label className="text-xs text-muted-foreground">Search lesson/question</label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="text contains..." />
          </div>
          <div className="w-28">
            <label className="text-xs text-muted-foreground">Limit</label>
            <Input type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value) || 100)} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={loadPersisted} disabled={loading} variant="default">{loading ? "Loading..." : "Load persisted proposals"}</Button>
          <Button onClick={runProposal} disabled={loading} variant="outline">{loading ? "Scanning..." : "Generate new proposals"}</Button>
          <Button variant="secondary" onClick={approveAll} disabled={!proposals.length}>Approve all visible</Button>
          <Button variant="secondary" onClick={rejectAllVisible} disabled={!proposals.length}>Reject all visible</Button>
          <Button variant="outline" onClick={() => bulkUpdateStatus("approved")} disabled={!approvedCount}>Bulk mark approved ({approvedCount})</Button>
          <Button variant="outline" onClick={() => bulkUpdateStatus("rejected")} disabled={!proposals.some((p) => rejected[p.question_id])}>Bulk mark rejected</Button>
          <Button variant="outline" onClick={exportCsv} disabled={!proposals.length}>Export CSV</Button>
          <Button onClick={applyApproved} disabled={applying || approvedCount === 0}>
            {applying ? "Applying..." : `Apply ${approvedCount} approved`}
          </Button>
        </div>
        {approvedCount > 50 && (
          <div className="rounded border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-xs">
            You're about to apply more than 50 tags in one batch. Double-check the selections before applying.
          </div>
        )}
      </Card>

      <div className="space-y-2">
        {proposals.map((p) => {
          const isSel = !!selected[p.question_id];
          const isRej = !!rejected[p.question_id];
          return (
            <Card key={p.question_id} className={`p-4 ${isRej ? "opacity-50" : ""}`}>
              <div className="flex justify-between gap-4">
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground">
                    {p.course_name ?? "—"} · {p.lesson_name ?? "—"}
                  </div>
                  <div className="text-sm">{p.question_preview}</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline">proposed: {p.proposed_concept_tag}</Badge>
                    <Badge variant="secondary">confidence: {p.confidence_score}</Badge>
                    {p.current_concept_tag && <Badge>current: {p.current_concept_tag}</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.reason}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant={isSel ? "default" : "outline"}
                    onClick={() => { setSelected((s) => ({ ...s, [p.question_id]: !s[p.question_id] })); setRejected((r) => ({ ...r, [p.question_id]: false })); }}>
                    {isSel ? "Approved" : "Approve"}
                  </Button>
                  <Button size="sm" variant={isRej ? "destructive" : "outline"}
                    onClick={() => { setRejected((r) => ({ ...r, [p.question_id]: !r[p.question_id] })); setSelected((s) => ({ ...s, [p.question_id]: false })); }}>
                    {isRej ? "Rejected" : "Reject"}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {!proposals.length && !loading && (
          <p className="text-sm text-muted-foreground">No proposals yet. Click "Generate proposals" to start.</p>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}